import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowUpRight,
  TrendingUp,
  Users,
  MapPin,
  Clock,
  Check,
} from "lucide-react";
import PageShell from "@/components/site/PageShell";
import FinalCTA from "@/components/site/FinalCTA";
import { getProjectBySlug, PROJECTS } from "@/lib/projects";

const CHALLENGE = [
  "Outdated WordPress site with 4-second load times",
  "Buried below 5 competitors for every money keyword",
  "Zero presence in Google's Map Pack",
  "Generic brand that didn't signal premium care",
];

const SOLUTION = [
  "Custom-coded site with sub-1s load times",
  "Full technical + on-page SEO overhaul",
  "Local SEO + Google Business Profile optimization",
  "Refined brand identity & photography",
  "Schema markup for service & FAQ pages",
  "Click-to-call & online booking integration",
];

const RESULTS = [
  { label: "Organic Traffic", value: "+248%", desc: "12-month YoY growth" },
  { label: "New Patients", value: "+312%", desc: "Booked through site" },
  { label: "Avg. Position", value: "#1.4", desc: "Across 40 keywords" },
  { label: "Load Speed", value: "0.9s", desc: "From 4.1s baseline" },
];

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const project = getProjectBySlug(slug);

  if (!project) {
    return (
      <PageShell testId="project-detail-not-found">
        <section className="section text-center">
          <h1 className="font-display text-4xl font-bold">
            Case study coming soon.
          </h1>
          <p className="mt-4 text-white/55">
            This case study isn&apos;t published yet.
          </p>
          <Link
            to="/projects"
            className="mt-8 inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-semibold"
          >
            <ArrowLeft className="w-4 h-4" /> Back to portfolio
          </Link>
        </section>
      </PageShell>
    );
  }

  const others = PROJECTS.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <PageShell testId="project-detail-page">
      <section className="relative pt-12 pb-10">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Link
            to="/projects"
            data-testid="project-detail-back"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-cyan-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All Projects
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-8"
          >
            <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-400 font-semibold">
              {project.industry}
            </div>
            <h1 className="mt-3 font-display text-5xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
              {project.name}
            </h1>

            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-white/55">
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-cyan-400" /> {project.location}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-cyan-400" /> {project.duration}
              </span>
              <span className="inline-flex items-center gap-1.5">
                Services: <span className="text-white">{project.services}</span>
              </span>
              <a
                href={project.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="project-detail-live"
                className="inline-flex items-center gap-1.5 text-cyan-400 hover:text-cyan-300 font-semibold"
              >
                Visit live site <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="relative pb-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="rounded-3xl overflow-hidden border border-white/10 bg-[#0a0a0a]">
            <img
              src={project.img}
              alt={project.name}
              className="w-full h-auto block"
            />
          </div>
        </div>
      </section>

      {/* Results bar */}
      <section className="relative pb-16" data-testid="project-results">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="rounded-3xl border border-white/10 bg-gradient-to-r from-blue-600/[0.08] via-cyan-400/[0.05] to-blue-600/[0.08] p-8 md:p-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {RESULTS.map((r) => (
                <div key={r.label}>
                  <div className="font-display text-3xl md:text-4xl font-extrabold text-gradient-brand">
                    {r.value}
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-widest text-white/45 font-semibold">
                    {r.label}
                  </div>
                  <div className="text-sm text-white/60">{r.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="relative pb-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-3 gap-10">
          <div className="md:col-span-1">
            <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
              Overview
            </div>
            <h2 className="mt-2 font-display text-3xl font-bold">
              The story behind the numbers.
            </h2>
          </div>
          <div className="md:col-span-2 text-lg text-white/70 leading-relaxed">
            {project.summary}
          </div>
        </div>
      </section>

      {/* Challenge + Solution */}
      <section className="relative pb-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-6">
          <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-8">
            <div className="text-[10px] uppercase tracking-widest text-white/45 font-semibold">
              The Challenge
            </div>
            <h3 className="mt-2 font-display text-2xl font-bold">
              Stuck on page 2 with a slow, dated site.
            </h3>
            <ul className="mt-6 space-y-3">
              {CHALLENGE.map((c) => (
                <li key={c} className="flex items-start gap-3 text-white/70">
                  <span className="mt-1 w-5 h-5 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0 text-xs text-white/40">
                    ×
                  </span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl p-[1px] bg-gradient-to-br from-blue-600 to-cyan-400">
            <div className="rounded-[calc(1.5rem-1px)] bg-[#0a0a0a] p-8 h-full">
              <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
                What We Did
              </div>
              <h3 className="mt-2 font-display text-2xl font-bold">
                Rebuilt the entire growth stack.
              </h3>
              <ul className="mt-6 space-y-3">
                {SOLUTION.map((s) => (
                  <li key={s} className="flex items-start gap-3 text-white">
                    <span className="mt-1 w-5 h-5 rounded-full bg-gradient-to-br from-blue-600 to-cyan-400 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-black" />
                    </span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Stats restated */}
      <section className="relative pb-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-3xl border border-white/10 bg-white/[0.025] p-8">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/45 font-semibold">
                <TrendingUp className="w-3 h-3 text-cyan-400" /> Traffic Growth
              </div>
              <div className="mt-3 font-display text-5xl font-extrabold text-gradient-brand">
                {project.traffic}
              </div>
              <p className="mt-3 text-white/55">
                Year-over-year organic search traffic increase driven by
                content, technical SEO and GEO optimization.
              </p>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/[0.025] p-8">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/45 font-semibold">
                <Users className="w-3 h-3 text-cyan-400" /> Lead Growth
              </div>
              <div className="mt-3 font-display text-5xl font-extrabold text-gradient-brand">
                {project.leads}
              </div>
              <p className="mt-3 text-white/55">
                Qualified inbound leads booked directly through the new website
                & local search.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Other projects */}
      <section className="relative pb-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-8">
            <h2 className="font-display text-3xl font-bold">More projects</h2>
            <Link
              to="/projects"
              className="text-sm text-cyan-400 hover:text-cyan-300 font-semibold"
            >
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {others.map((p, i) => (
              <Link
                key={p.slug}
                to={`/projects`}
                data-testid={`related-project-${i}`}
                className="group rounded-3xl border border-white/10 bg-white/[0.025] overflow-hidden hover:border-cyan-500/30 transition-all"
              >
                <div className="aspect-[16/10] overflow-hidden bg-[#0a0a0a]">
                  <img
                    src={p.img}
                    alt={p.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
                    {p.industry}
                  </div>
                  <div className="mt-1 font-display text-lg font-semibold">
                    {p.name}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </PageShell>
  );
}
